import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Grid,
  Button,
  Paper,
  TextField,
  Typography,
  InputLabel,
  CircularProgress,
  Collapse,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { login, resendOtp, verifyOtp } from "../api/action";
import FingerprintJS from "@fingerprintjs/fingerprintjs";
import CustomSnackbar from "../Utils/CustomSnackbar";
import { Error } from "@mui/icons-material";
import LocalHospitalIcon from "@mui/icons-material/LocalHospital";
import Logo from "../Image/Logo.png";
import { setUser } from "../redux/slices/userSlice";

const LoginScreen = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [emailError, setEmailError] = useState("");
  const [otpError, setOtpError] = useState("");
  const [loading, setLoading] = useState(false);
  const [isOtpVisible, setIsOtpVisible] = useState(false);
  const [deviceId, setDeviceId] = useState("");

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("");

  const [modalOpen, setModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState("");
  const [modalSeverity, setModalSeverity] = useState("");

  const [otpExpiryTime, setOtpExpiryTime] = useState(0);

  const otpInputRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Device Fingerprint
  const getFingerprint = async () => {
    const fp = await FingerprintJS.load();
    const result = await fp.get();
    return result.visitorId;
  };

  useEffect(() => {
    getFingerprint().then((visitorId) => setDeviceId(visitorId));
  }, []);

  // Auto-focus OTP when visible
  useEffect(() => {
    if (isOtpVisible && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [isOtpVisible]);

  // Countdown effect for OTP expiration
  useEffect(() => {
    if (otpExpiryTime > 0) {
      const timer = setTimeout(() => setOtpExpiryTime(otpExpiryTime - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [otpExpiryTime]);

  // Format mm:ss
  const formatTime = (seconds) => {
    const m = String(Math.floor(seconds / 60)).padStart(2, "0");
    const s = String(seconds % 60).padStart(2, "0");
    return `${m}:${s}`;
  };

  // Validation
  const validateEmail = (value) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(value);
    setEmailError(isValid ? "" : "Invalid email address");
    return isValid;
  };

  const handleEmailChange = (e) => {
    setEmail(e.target.value);
    validateEmail(e.target.value);
  };

  const handlePasswordChange = (e) => setPassword(e.target.value);

  const handleOtpChange = (e) => {
    const value = e.target.value.replace(/\D/g, "");
    setOtp(value);
    setOtpError(value.length !== 6 ? "OTP must be 6 digits" : "");
  };

  // Snackbar + Modal
  const handleSnackbarClose = () => setSnackbarOpen(false);
  const handleModalClose = () => setModalOpen(false);

  const openModal = (message, severity) => {
    setModalMessage(message);
    setModalSeverity(severity);
    setModalOpen(true);
  };

  // API Calls
  const handleLoginClick = (e) => {
    e.preventDefault();
    if (!validateEmail(email)) return;

    if (!email || !password) {
      setSnackbarMessage("Please fill all the fields.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    localStorage.clear();
    sessionStorage.clear();

    const payload = { email, password, device_id: deviceId };
    setLoading(true);

    dispatch(
      login(
        payload,
        (data) => {
          setLoading(false);
          if (data?.data?.msg === "Login Success") {
            saveUserData(data?.data);
          } else if (
            data?.data?.msg ===
            "OTP sent to your email, Please verify. It will expire in 10 minutes."
          ) {
            setSnackbarMessage(data?.data?.msg);
            setSnackbarSeverity("success");
            setSnackbarOpen(true);
            setIsOtpVisible(true);
            setOtpExpiryTime(600); // 10 min expiry
          } else {
            setSnackbarMessage(data?.data?.msg);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          }
        },
        (error) => {
          console.log(error)
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.msg ||
                error.response?.data?.error ||
                error.response?.data?.detail ||
              "Oops! Please login again for security.";
          openModal(errorMessage, "error");
        }
      )
    );
  };

  const handleVerifyOtp = (e) => {
    e.preventDefault();
    if (otpExpiryTime <= 0) {
      setSnackbarMessage("OTP expired. Please resend OTP.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = { email, otp, device_id: deviceId };
    setLoading(true);

    dispatch(
      verifyOtp(
        payload,
        (data) => {
          setLoading(false);
          if (data?.data?.msg === "Login Success") {
            saveUserData(data?.data);
          } else {
            setSnackbarMessage(data?.data?.msg);
            setSnackbarSeverity("error");
            setSnackbarOpen(true);
          }
        },
        (error) => {
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.msg ||
                error.response?.data?.error ||
                error.response?.data?.detail ||
              "Oops! Please login again for security.";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  // Resend OTP
  const handleResendOtp = () => {
    if (!email) {
      setSnackbarMessage("Please enter your email and password first.");
      setSnackbarSeverity("error");
      setSnackbarOpen(true);
      return;
    }

    const payload = { email };
    setLoading(true);

    dispatch(
      resendOtp(
        payload,
        (data) => {
          setLoading(false);

          setSnackbarMessage(data?.data?.msg);
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
          setIsOtpVisible(true); // show OTP input again
          setOtp(""); // clear old OTP
          setOtpExpiryTime(600); // reset 10 min expiry
        },
        (error) => {
          setLoading(false);
          const errorMessage =
            error.message === "Network Error"
              ? error.message
              : error.response?.data?.msg ||
                error.response?.data?.error ||
                "Failed to resend OTP. Try again.";
          setSnackbarMessage(errorMessage);
          setSnackbarSeverity("error");
          setSnackbarOpen(true);
        }
      )
    );
  };

  // Save user data + navigate
  // const saveUserData = (data) => {
  //   const {
  //     token,
  //     profile_id,
  //     user_id,
  //     user_name,
  //     user_email,
  //     user_role,
  //     is_deleted_access,
  //     force_password_change,
  //   } = data;

  //   localStorage.setItem("accessToken", token?.access);
  //   localStorage.setItem("refreshToken", token?.refresh);
  //   localStorage.setItem("userEmail", user_email);
  //   localStorage.setItem("userId", user_id);
  //   localStorage.setItem("userName", user_name);
  //   localStorage.setItem("profileId", profile_id);
  //   localStorage.setItem("userRole", user_role);
  //   localStorage.setItem("is_deleted_access", is_deleted_access);
  //   localStorage.setItem("forcePassword", force_password_change);

  //   setSnackbarMessage(data.msg);
  //   setSnackbarOpen(true);
  //   setSnackbarSeverity("success");

  //   switch (user_role) {
  //     case "Biller":
  //       navigate("/biller-dashboard");
  //       break;
  //     case "Provider":
  //     case "Employee":
  //     case "Employee_Office_Work":
  //     case "Consultant":
  //     case "Employee_Admin_Work":
  //       navigate("/providerdashboard");
  //       break;
  //     default:
  //       navigate("/dashboard");
  //       break;
  //   }
  // };

  const saveUserData = (data) => {
    const {
      token,
      profile_id,
      user_id,
      user_name,
      user_email,
      user_role,
      company_code,
      company_logo,
      company_profile,
      access_level,
      is_activated,
      user_profile_picture,
      is_deleted_access,
      force_password_change,
    } = data;

    console.log("dev".data)

    const payload = {
      userId: user_id,
      userName: user_name,
      userEmail: user_email,
      accessToken: token?.access,
      refreshToken: token?.refresh,
      userRole: user_role,
      companyCode: company_code || null,
      companyLogo: company_logo || null,
      companyProfile: company_profile || null,
      accessLevel: access_level || null,
      isActivated: is_activated || null,
      userProfilePicture: user_profile_picture || null,

      profileId: profile_id,
      isDeletedAccess: is_deleted_access,
      forcePassword: force_password_change,
    };

    dispatch(setUser(payload));
    setSnackbarMessage("Login Successful");
    setSnackbarSeverity("success");
    setSnackbarOpen(true);

    switch (user_role) {
      case "Biller":
        navigate("/biller-dashboard");
        break;
      case "Provider":
      case "Employee":
      case "Employee_Office_Work":
      case "Consultant":
      case "Employee_Admin_Work":
        navigate("/providerdashboard");
        break;
      default:
        navigate("/dashboard");
        break;
    }
  };

  return (
    <Grid container sx={{ height: "100vh" }}>
      {/* Left Side - Branding */}
      <Grid
        item
        xs={false}
        md={6}
        sx={{
          position: "relative",
          backgroundSize: "cover",
          backgroundPosition: "center",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 4,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background:
              "linear-gradient(to bottom right, rgba(29, 57, 105, 1), rgba(0, 0, 0, 1))",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "relative",
            zIndex: 2,
            textAlign: "center",
            color: "#fff",
            maxWidth: 400,
          }}
        >
          <img
            src={Logo}
            alt="Healthcare Logo"
            style={{
              width: "100%",
              height: "100%",
              background: "#fff",
              padding: "8px",
              borderRadius: "12px",
            }}
          />
        </Box>
      </Grid>

      {/* Right Side - Login Form */}
      <Grid
        item
        xs={12}
        md={6}
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          background: "#f9fafb",
          p: 2,
        }}
      >
        <Paper
          elevation={3}
          sx={{ maxWidth: 420, width: "100%", p: 4, borderRadius: "16px" }}
        >
          <Box textAlign="center" mb={3}>
            <LocalHospitalIcon
              sx={{ fontSize: 50, color: "rgba(29, 57, 105, 1)" }}
            />
            <Typography variant="h5" fontWeight="bold">
              Billing Portal
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Sign in to continue to your dashboard
            </Typography>
          </Box>

          <InputLabel>Email</InputLabel>
          <TextField
            fullWidth
            margin="dense"
            value={email}
            onChange={handleEmailChange}
            helperText={emailError}
            error={!!emailError}
          />

          <InputLabel>Password</InputLabel>
          <TextField
            fullWidth
            margin="dense"
            type="password"
            value={password}
            onChange={handlePasswordChange}
          />

          {/* OTP Section */}
          <Collapse in={isOtpVisible} timeout={400}>
            {otpExpiryTime > 0 ? (
              <Box>
                <InputLabel>OTP</InputLabel>
                <TextField
                  fullWidth
                  margin="dense"
                  value={otp}
                  onChange={handleOtpChange}
                  inputRef={otpInputRef}
                  inputProps={{ maxLength: 6 }}
                  helperText={otpError}
                  error={!!otpError}
                />

                {/* OTP Expiration Timer */}
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 13,
                    color: "red",
                    textAlign: "right",
                  }}
                >
                  OTP expires in {formatTime(otpExpiryTime)}
                </Typography>
              </Box>
            ) : (
              <Box>
                <Typography
                  sx={{
                    mt: 1,
                    fontSize: 14,
                    textAlign: "right",
                    cursor: "pointer",
                    color: "rgba(29, 57, 105, 1)",
                    textDecoration: "underline",
                  }}
                  onClick={handleResendOtp}
                >
                  Resend OTP
                </Typography>
              </Box>
            )}
          </Collapse>

          <Typography
            sx={{
              textAlign: "right",
              mt: 1,
              color: "rgba(29, 57, 105, 1)",
              fontSize: 14,
              cursor: "pointer",
              textDecoration: "underline",
            }}
            onClick={() => navigate("/forget-password")}
          >
            Forgot Password?
          </Typography>

          <Button
            fullWidth
            variant="contained"
            disableElevation
            sx={{
              mt: 3,
              py: 1.5,
              background: "rgba(29, 57, 105, 1)",
              fontWeight: "bold",
              borderRadius: "8px",
              color: "#ffffff",
            }}
            onClick={isOtpVisible ? handleVerifyOtp : handleLoginClick}
            disabled={
              loading ||
              (isOtpVisible && (otp.length < 6 || otpExpiryTime <= 0))
            }
          >
            {loading ? (
              <CircularProgress size={18} sx={{ color: "#fff" }} />
            ) : isOtpVisible ? (
              "Verify OTP"
            ) : (
              "Login"
            )}
          </Button>
        </Paper>
      </Grid>

      {/* Modal */}
      <Dialog
        open={modalOpen}
        onClose={handleModalClose}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle
          sx={{
            color: modalSeverity === "error" ? "red" : "rgba(29, 57, 105, 1)",
          }}
        >
          {modalSeverity === "error" && <Error sx={{ mr: 1 }} />}
          Attention
        </DialogTitle>
        <DialogContent>
          <Typography>{modalMessage}</Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleModalClose}
            sx={{ fontWeight: "bold" }}
          >
            OK
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <CustomSnackbar
        open={snackbarOpen}
        handleClose={handleSnackbarClose}
        message={snackbarMessage}
        severity={snackbarSeverity}
      />


    </Grid>
  );
};

export default LoginScreen;
